import {
  MONTHS,
  parseAmount,
  parseDate,
  detectCurrencySymbol,
  findDateColumn,
  findAmountColumn,
} from "./utils";

function detectUnit(rows, headers, amountCol) {
  const prefix = detectCurrencySymbol(rows, amountCol);
  if (prefix) return { symbol: prefix, position: "prefix" };
  const unitCol = headers.find((h) => /charity_\d+_unit/i.test(h));
  if (unitCol) {
    for (const row of rows) {
      const u = row[unitCol];
      if (u) return { symbol: u, position: "suffix" };
    }
  }
  return { symbol: "", position: "suffix" };
}

const SummaryCard = ({ label, value }) => (
  <div className="flex-1 min-w-0 bg-neutral-50 rounded-lg p-4 border border-neutral-200">
    <p className="text-xs text-neutral-500 mb-1">{label}</p>
    <p className="text-xl font-bold text-primary-700">{value}</p>
  </div>
);

const DonationsSummary = ({ rows, headers }) => {
  const dateCol = findDateColumn(headers);
  const amountCol = findAmountColumn(headers, rows);

  if (!amountCol) return null;

  const { symbol, position } = detectUnit(rows, headers, amountCol);
  const fmt = (n) => {
    const formatted = n.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    if (!symbol) return formatted;
    return position === "prefix"
      ? `${symbol}${formatted}`
      : `${formatted} ${symbol}`;
  };

  const cumulativeTotal = rows.reduce(
    (sum, r) => sum + (parseAmount(r[amountCol]) ?? 0),
    0,
  );

  let latestPeriodTotal = null;
  let latestPeriodLabel = null;
  if (dateCol) {
    const dated = rows
      .map((r) => ({ row: r, date: parseDate(r[dateCol]) }))
      .filter((x) => x.date !== null);

    if (dated.length > 0) {
      const maxTime = Math.max(...dated.map((x) => x.date.getTime()));
      const maxDate = new Date(maxTime);
      const yr = maxDate.getFullYear();
      const mo = maxDate.getMonth();
      const day = maxDate.getDate();
      latestPeriodTotal = dated
        .filter(
          ({ date }) =>
            date.getFullYear() === yr &&
            date.getMonth() === mo &&
            date.getDate() === day,
        )
        .reduce((sum, { row }) => sum + (parseAmount(row[amountCol]) ?? 0), 0);
      latestPeriodLabel = maxDate.toLocaleDateString(undefined, {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
  }

  const now = new Date();
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthTotal = rows
    .filter((r) => {
      if (!dateCol) return false;
      const d = parseDate(r[dateCol]);
      return (
        d &&
        d.getFullYear() === lastMonthDate.getFullYear() &&
        d.getMonth() === lastMonthDate.getMonth()
      );
    })
    .reduce((sum, r) => sum + (parseAmount(r[amountCol]) ?? 0), 0);

  return (
    <div className="mb-2">
      <h4 className="text-sm font-semibold text-neutral-600 mb-3">Summary</h4>
      <div className="flex gap-4">
        <SummaryCard label="Cumulative Total" value={fmt(cumulativeTotal)} />
        {latestPeriodTotal !== null && (
          <SummaryCard
            label={`Latest Period${latestPeriodLabel ? ` (${latestPeriodLabel})` : ""}`}
            value={fmt(latestPeriodTotal)}
          />
        )}
        <SummaryCard
          label={`${MONTHS[lastMonthDate.getMonth()]} Total`}
          value={fmt(lastMonthTotal)}
        />
      </div>
    </div>
  );
};

export default DonationsSummary;
